import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from 'src/context/useTenantContext';
import { PaletteItem } from './types';
import { useGlobalSearch } from './useGlobalSearch';

interface GlobalSearchModalProps {
  open: boolean;
  onClose: () => void;
  initialQuery?: string;
}

/**
 * The palette proper. Mounted only while open, so `initialQuery` seeds `useState` directly
 * and closing resets everything by unmounting — no effect has to write state for either.
 */
const GlobalSearchPalette = ({ onClose, initialQuery }: Omit<GlobalSearchModalProps, 'open'>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { buildPath } = useTenantContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState(initialQuery ?? '');
  // A changed query invalidates the old position, so the index is stored next to the query it
  // belongs to and reads back as 0 for any newer one. Deriving the reset beats resetting from
  // an effect, which would cost a second render pass on every keystroke.
  const [highlightState, setHighlightState] = useState({ query: initialQuery ?? '', index: 0 });
  const highlight = highlightState.query === query ? highlightState.index : 0;

  const moveHighlight = useCallback(
    (next: (current: number) => number) =>
      setHighlightState((state) => ({ query, index: next(state.query === query ? state.index : 0) })),
    [query]
  );

  const { sections, items, records } = useGlobalSearch(query);

  // Focus is browser state, not React state, so an effect is the right tool. A fresh mount per
  // opening means it runs exactly once per open.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const activeItem: PaletteItem | undefined = items[highlight];

  const activate = useCallback(
    (item: PaletteItem | undefined) => {
      if (!item) return;
      if (item.run) item.run();
      else if (item.path) {
        const search = item.params ? `?${new URLSearchParams(item.params).toString()}` : '';
        navigate(`${buildPath(item.path)}${search}`);
      }
      onClose();
    },
    [buildPath, navigate, onClose]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      // The dialog declares the background inert via aria-modal; never let Tab
      // move real focus out of the input into it.
      if (event.key === 'Tab') {
        event.preventDefault();
        return;
      }
      if (!items.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveHighlight((current) => (current + 1) % items.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveHighlight((current) => (current - 1 + items.length) % items.length);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        activate(items[highlight]);
      }
    },
    [activate, highlight, items, moveHighlight, onClose]
  );

  const listboxId = useMemo(() => 'global-search-listbox', []);

  return (
    <div
      className="global-search__overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {/*
        jsx-a11y/prefer-tag-over-role is off for this subtree on purpose. It wants <dialog>,
        <select>/<datalist> and <option> for the roles below, but this is the ARIA combobox
        popup pattern: the input keeps DOM focus and drives a virtual cursor through
        aria-activedescendant, which native form controls cannot express. <dialog> would also
        force the imperative showModal() lifecycle onto a component React already mounts and
        unmounts. interactive-supports-focus is off for the same reason — options in this
        pattern are deliberately not focusable, since focus never leaves the input.
      */}
      {/* eslint-disable jsx-a11y/prefer-tag-over-role, jsx-a11y/interactive-supports-focus */}
      <div className="global-search__panel" role="dialog" aria-modal="true" aria-label={t('globalSearch.dialogLabel')}>
        <input
          ref={inputRef}
          className="global-search__input"
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeItem?.id}
          placeholder={t('globalSearch.placeholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />

        <div
          className="global-search__results"
          id={listboxId}
          role="listbox"
          aria-label={t('globalSearch.dialogLabel')}
        >
          {records.isFetching && <div className="global-search__searching">{t('globalSearch.searching')}</div>}

          {records.isError && (
            <div className="global-search__records-failed">
              <span>{t('globalSearch.recordsFailed')}</span>
              <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => records.retry()}>
                {t('globalSearch.retry')}
              </button>
            </div>
          )}

          {!records.isFetching && !records.isError && sections.length === 0 && (
            <div className="global-search__empty">{t('globalSearch.noResults', { query })}</div>
          )}

          {sections.map((section) => (
            <div key={section.id} role="group" aria-labelledby={`global-search-section-${section.id}`}>
              <div className="global-search__section-title" id={`global-search-section-${section.id}`}>
                {section.title ?? t(`globalSearch.sections.${section.id}`)}
              </div>
              {section.items.map((item) => (
                <div
                  key={item.id}
                  id={item.id}
                  role="option"
                  className="global-search__option"
                  aria-selected={item.id === activeItem?.id}
                  onMouseDown={(event) => {
                    event.preventDefault(); // keep focus in the input
                    activate(item);
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="global-search__footer">
          <span>↑↓ {t('globalSearch.hintNavigate')}</span>
          <span>↵ {t('globalSearch.hintOpen')}</span>
          <span>esc {t('globalSearch.hintClose')}</span>
        </div>
      </div>
      {/* eslint-enable jsx-a11y/prefer-tag-over-role, jsx-a11y/interactive-supports-focus */}
    </div>
  );
};

export const GlobalSearchModal = ({ open, onClose, initialQuery }: GlobalSearchModalProps) => {
  if (!open) return null;
  return <GlobalSearchPalette onClose={onClose} initialQuery={initialQuery} />;
};
